/**
 * PhaseDashSkillEffect — Specter wraith-path Evasion upgrade: wall-phasing blink.
 *
 * Upgrade enhancing Blink (via tree position). On equip, Blink passes through
 * thin walls (1-tile-thick obstacles). The collision system checks
 * `blinkIgnoresThinWalls` during Blink's reposition to skip wall tiles that
 * have walkable space on the opposite side.
 *
 * On equip: sets `blinkIgnoresThinWalls` on DashComponent.
 * On unequip: resets the flag.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Phase Dash
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PhaseDashDeps {
  readonly dash: {
    /** When true, Blink passes through thin walls (1-tile-thick obstacles). */
    blinkIgnoresThinWalls: boolean;
  };
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PhaseDashSkillEffect implements SkillEffect {
  readonly skillId = 'phase_dash';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: PhaseDashDeps;

  /** Whether the flag has been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: PhaseDashDeps) {
    this.deps = deps;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    // Apply thin-wall passthrough flag on first update tick (equip).
    if (!this._applied) {
      this.deps.dash.blinkIgnoresThinWalls = true;
      this._applied = true;
    }
  }

  destroy(): void {
    // Reset flag on unequip
    if (this._applied) {
      this.deps.dash.blinkIgnoresThinWalls = false;
      this._applied = false;
    }
  }
}
